import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@toki/auth';
import {
  CaretDown,
  CaretLeft,
  Check,
  ClockCounterClockwise,
  Columns,
  LockSimple,
  MagnifyingGlass,
  X,
} from '@phosphor-icons/react';
import type { AuditEntry, RolePermission, RoleWithPermissions } from '../services/api';
import { listAllPermissions, listAudit, listRoles, updateRolesPermissions } from '../services/api';
import { Topbar } from '../components/Topbar';
import { useDialog } from '../components/useDialog';
import { usePermissions } from '../hooks/usePermissions';
import { getModuleColor } from '../config/modules';
import { getModuleIcon } from '../icons';
import { ROLE_DESCRIPTIONS, roleLabel } from '../config/roles';
import styles from './Roles.module.css';

/*
 * Roles y permisos (paso 48a), según el v5 de Jose (`docs/diseno/kivai-tickets-v5.html`).
 *
 * Lo que el v5 dibuja y aquí NO está, a propósito: roles personalizados
 * («Nuevo rol», «Duplicar», «Basado en», la sección «Personalizados»), que son
 * el 48b y no se hacen hasta que hagan falta.
 *
 * Tres reglas que no son de diseño:
 * - Edita solo quien tiene `system:manage_roles`. El resto del staff ve LO
 *   MISMO con los interruptores deshabilitados y sin barra. La puerta de verdad
 *   es el backend (403), esto es lo que se pinta.
 * - Superadmin y Usuario (`requester`) no se editan, y la pantalla enseña lo
 *   que TIENEN de hecho, no lo que diga la tabla: el superadmin pasa por nombre
 *   (todos) y un cliente no tiene ninguno (K60, `user_permissions` en deps.py).
 * - Se guarda en LOTE: «N cambios en M roles» se guardan todos o ninguno.
 */

const mc = getModuleColor('roles');
const ModuleIcon = getModuleIcon('roles');

/** El orden del v5: de más a menos poder. Un rol que no esté aquí va al final. */
const ROLE_ORDER = ['superadmin', 'admin', 'coordinator', 'agent', 'requester'];
const LOCKED = new Set(['superadmin', 'requester']);
const LOCK_REASON: Record<string, string> = {
  superadmin: 'Tiene todos los permisos siempre. No se le pueden quitar.',
  requester: 'Es la cuenta de un cliente: no entra al panel, así que no tiene permisos. Lo que ve en el portal son sus tickets.',
};

/** Los grupos del v5, en su orden. El nombre del permiso es su `description`
 *  de la BD; aquí solo se pone nombre al MÓDULO. */
const MODULES: [string, string][] = [
  ['system', 'Sistema'],
  ['areas', 'Equipos'],
  ['categories', 'Categorías'],
  ['clientes', 'Clientes'],
  ['incidencias', 'Tickets'],
  ['informes', 'Informes'],
  ['sla', 'SLA'],
  ['users', 'Usuarios'],
];

const MAX_CMP = 5;
const code = (p: RolePermission) => `${p.module}:${p.action}`;
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const usuarios = (n: number) => (n === 1 ? '1 usuario' : `${n} usuarios`);
const sameSet = (a: Set<string>, b: Set<string>) => a.size === b.size && [...a].every((x) => b.has(x));

interface Group {
  key: string;
  name: string;
  perms: RolePermission[];
}

export function RolesScreen() {
  const { provider } = useAuth();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('system:manage_roles');
  const canAudit = hasPermission('system:view_audit');
  const { confirm, element: dialog } = useDialog();

  const [roles, setRoles] = useState<RoleWithPermissions[]>([]);
  const [allPerms, setAllPerms] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** Lo que se ha tocado y no se ha guardado: nombre de rol → conjunto de
   *  códigos ENTERO. Un rol que vuelve a como estaba sale de aquí. */
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  const [sel, setSel] = useState('agent');
  const [rolQ, setRolQ] = useState('');
  const [permQ, setPermQ] = useState('');
  const [comparing, setComparing] = useState(false);
  const [cmp, setCmp] = useState<string[]>([]);
  const [onlyDiff, setOnlyDiff] = useState(true);
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const [auditOpen, setAuditOpen] = useState(false);

  const load = useCallback(async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setLoading(true);
    try {
      const [r, p] = await Promise.all([listRoles(token), listAllPermissions(token)]);
      setRoles(r);
      setAllPerms(p);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { load(); }, [load]);

  const ordered = useMemo(
    () => [...roles].sort((a, b) => {
      const ia = ROLE_ORDER.indexOf(a.name), ib = ROLE_ORDER.indexOf(b.name);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.name.localeCompare(b.name);
    }),
    [roles],
  );
  const byName = useMemo(() => Object.fromEntries(roles.map((r) => [r.name, r])), [roles]);
  const allCodes = useMemo(() => allPerms.map(code), [allPerms]);
  const total = allCodes.length;

  const groups: Group[] = useMemo(() => {
    const known = MODULES.map(([key, name]) => ({
      key, name,
      perms: allPerms.filter((p) => p.module === key).sort((a, b) => code(a).localeCompare(code(b))),
    }));
    // Un módulo nuevo que aún no tenga nombre aquí sale igual, con su clave.
    const extra = [...new Set(allPerms.map((p) => p.module))]
      .filter((m) => !MODULES.some(([k]) => k === m))
      .map((m) => ({ key: m, name: m, perms: allPerms.filter((p) => p.module === m) }));
    return [...known, ...extra].filter((g) => g.perms.length > 0);
  }, [allPerms]);

  /** Lo que el rol TIENE de hecho, guardado. */
  const saved = useCallback((name: string): Set<string> => {
    if (name === 'superadmin') return new Set(allCodes);
    if (name === 'requester') return new Set();
    return new Set((byName[name]?.permissions ?? []).map(code));
  }, [allCodes, byName]);
  const cur = useCallback((name: string) => draft[name] ?? saved(name), [draft, saved]);
  const has = (name: string, c: string) => cur(name).has(c);
  const changed = (name: string, c: string) => cur(name).has(c) !== saved(name).has(c);
  const editable = (name: string) => canEdit && !LOCKED.has(name);

  const setPerms = (name: string, codes: string[], on: boolean) => {
    if (!editable(name)) return;
    setNotice(null);
    setDraft((d) => {
      const next = new Set(d[name] ?? saved(name));
      codes.forEach((c) => (on ? next.add(c) : next.delete(c)));
      const out = { ...d };
      if (sameSet(next, saved(name))) delete out[name];
      else out[name] = next;
      return out;
    });
  };

  const drafts = Object.entries(draft);
  const dirtyRoles = drafts.map(([r]) => r);
  const nChanges = drafts.reduce((n, [r, a]) => {
    const b = saved(r);
    return n + [...a].filter((x) => !b.has(x)).length + [...b].filter((x) => !a.has(x)).length;
  }, 0);

  const handleSave = async () => {
    // El técnico tiene «Cogerse un ticket» y NO «Asignar ticket»: así solo coge
    // tickets libres (paso 41). Darle el segundo le quita ese límite sin que
    // nada lo diga, así que se pregunta (D1, Jose: «pop-up al guardar»).
    const unbounded = drafts
      .filter(([r, a]) => a.has('incidencias:assign') && a.has('incidencias:assign_self') && !saved(r).has('incidencias:assign'))
      .map(([r]) => r);
    if (unbounded.length) {
      const names = unbounded.map(roleLabel);
      const quien = names.length === 1 ? `el rol ${names[0]}` : `los roles ${names.slice(0, -1).join(', ')} y ${names.at(-1)}`;
      const ok = await confirm({
        title: 'Podrán reasignar cualquier ticket',
        message: (
          <>
            <p>Ahora mismo, quien tiene {quien} solo puede coger tickets libres.</p>
            <p>Con «Asignar ticket» podrá pasarle cualquier ticket a otra persona, o quitárselo a quien lo esté llevando.</p>
          </>
        ),
        confirmLabel: 'Guardar igualmente',
        cancelLabel: 'Volver',
      });
      if (!ok) return;
    }
    const token = await provider.getAccessToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const idOf = new Map(allPerms.map((p) => [code(p), p.id]));
      const fresh = await updateRolesPermissions(
        drafts.map(([r, a]) => ({
          roleId: byName[r]?.id ?? '',
          permissionIds: allCodes.filter((c) => a.has(c)).map((c) => idOf.get(c) ?? ''),
        })),
        token,
      );
      setRoles(fresh);
      setDraft({});
      setNotice(dirtyRoles.length === 1 ? 'Cambios guardados.' : `Cambios guardados en ${dirtyRoles.length} roles.`);
    } catch (e: any) {
      // Todo o nada: si falla, la BD sigue como estaba y el borrador también.
      setError(`No se ha guardado nada. ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.loading}>Cargando roles…</div>;

  const role = byName[sel] ?? ordered[0];
  if (!role) return <div className={styles.loading}>{error ?? 'No hay roles.'}</div>;

  // ── Lista ──
  const rq = norm(rolQ.trim());
  const listed = ordered.filter((r) => !rq || norm(r.name).includes(rq) || norm(roleLabel(r.name)).includes(rq));

  // ── Editor ──
  const pq = norm(permQ.trim());
  const match = (p: RolePermission) => !pq || norm(code(p)).includes(pq) || norm(p.description).includes(pq);
  let nMatch = 0;
  const editorGroups = groups
    .map((g) => {
      const vis = g.perms.filter(match);
      nMatch += vis.length;
      const byGroupName = !!pq && norm(g.name).includes(pq);
      return { ...g, show: vis.length > 0 || byGroupName, rows: vis.length ? vis : g.perms };
    })
    .filter((g) => g.show);

  // ── Comparar ──
  const openCompare = () => {
    const rest = ROLE_ORDER.filter((r) => r !== role.name && byName[r] && !LOCKED.has(r));
    setCmp([role.name, ...rest].slice(0, 3));
    setComparing(true);
  };
  const diffRows = (perms: RolePermission[]) =>
    onlyDiff && cmp.length > 1
      ? perms.filter((p) => { const v = cmp.map((r) => has(r, code(p))); return v.some((x) => x !== v[0]); })
      : perms;
  const cmpGroups = groups.map((g) => ({ ...g, rows: diffRows(g.perms) })).filter((g) => g.rows.length > 0);
  const cmpCols = { '--cmp-n': Math.max(cmp.length, 1) } as React.CSSProperties;

  const subtitle = canEdit
    ? 'Qué puede hacer cada rol en el panel'
    : 'Solo lectura: los permisos los cambia el superadmin';

  return (
    <div className={styles.page}>
      <Topbar
        title="Roles y permisos"
        subtitle={subtitle}
        moduleIcon={<ModuleIcon weight="duotone" size={18} color={mc.accent} />}
        moduleColor={mc.accent}
        moduleBg={mc.bg}
        actions={canAudit ? [{ label: 'Ver auditoría', onClick: () => setAuditOpen(true), variant: 'ghost' }] : undefined}
      />

      <div className={styles.body}>
        <aside className={styles.aside}>
          <div className={styles.asideSearch}>
            <label className={styles.search}>
              <MagnifyingGlass size={15} weight="bold" />
              <input value={rolQ} onChange={(e) => setRolQ(e.target.value)} placeholder="Buscar rol" aria-label="Buscar rol" />
              <span className={styles.searchCount}>{roles.length}</span>
            </label>
          </div>
          <div className={styles.roleList}>
            {listed.map((r) => {
              const active = comparing ? cmp.includes(r.name) : r.name === role.name;
              return (
                <button
                  key={r.id}
                  type="button"
                  className={`${styles.roleItem} ${active ? styles.roleItemActive : ''}`}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => { setSel(r.name); setComparing(false); }}
                >
                  <span className={styles.roleItemTop}>
                    <span className={styles.roleItemName}>{roleLabel(r.name)}</span>
                    {LOCKED.has(r.name) && <LockSimple size={12} weight="bold" className={styles.lockIcon} aria-label="No editable" />}
                    {draft[r.name] && <span className={styles.dirtyDot} aria-label="Con cambios sin guardar" />}
                    <span className={styles.roleItemCount}>{cur(r.name).size}</span>
                  </span>
                  <span className={styles.roleItemSub}>
                    <code>{r.name}</code> · {usuarios(r.activeUsers)}
                  </span>
                </button>
              );
            })}
            {listed.length === 0 && <div className={styles.listEmpty}>Ningún rol coincide.</div>}
          </div>
        </aside>

        <div className={styles.main}>
          {error && <div className={styles.error} role="alert">{error}</div>}
          {notice && <div className={styles.notice} role="status">{notice}</div>}

          {!comparing ? (
            <>
              <div className={styles.roleHead}>
                <div className={styles.roleHeadInfo}>
                  <div className={styles.roleHeadTitle}>
                    <span className={styles.roleHeadName}>{roleLabel(role.name)}</span>
                    <code className={styles.roleHeadCode}>{role.name}</code>
                    {LOCKED.has(role.name) && (
                      <span className={styles.lockPill} title={LOCK_REASON[role.name]}>
                        <LockSimple size={11} weight="bold" />No editable
                      </span>
                    )}
                  </div>
                  {/* El MISMO alto en todos los roles: con una tercera línea solo en
                      los bloqueados, la cabecera cambiaba al pasar de uno a otro y
                      todo lo de debajo saltaba (Jose, 48a). En escritorio es UNA
                      línea, como el v5; en estrecho, DOS fijas. El porqué del
                      candado va en el `title` de la píldora. */}
                  <div
                    className={styles.roleHeadSub}
                    title={`${ROLE_DESCRIPTIONS[role.name] ?? role.description} · ${usuarios(role.activeUsers)} · ${cur(role.name).size} de ${total} permisos`}
                  >
                    <span className={styles.subDesc}>{ROLE_DESCRIPTIONS[role.name] ?? role.description}</span>
                    <span className={styles.subSep}> · </span>
                    <span className={styles.subCounts}>{usuarios(role.activeUsers)} · {cur(role.name).size} de {total} permisos</span>
                  </div>
                </div>
                <div className={styles.roleHeadActions}>
                  <button type="button" className={styles.btn} onClick={openCompare}>
                    <Columns size={15} />Comparar
                  </button>
                </div>
              </div>

              <div className={styles.permSearchRow}>
                <label className={`${styles.search} ${styles.permSearch}`}>
                  <MagnifyingGlass size={15} weight="bold" />
                  <input
                    value={permQ}
                    onChange={(e) => setPermQ(e.target.value)}
                    placeholder="Buscar permiso, p. ej. asignar o users:"
                    aria-label="Buscar permiso"
                  />
                  {pq && (
                    <button type="button" className={styles.clear} onClick={() => setPermQ('')} aria-label="Limpiar">
                      <X size={11} weight="bold" />
                    </button>
                  )}
                </label>
                {pq && <span className={styles.permSearchCount}>{nMatch === 1 ? '1 permiso encontrado' : `${nMatch} permisos encontrados`}</span>}
              </div>

              <div className={styles.scroll}>
                {editorGroups.length === 0 && <div className={styles.emptyCard}>Ningún permiso coincide con «{permQ}».</div>}
                <div className={styles.groupGrid}>
                  {editorGroups.map((g) => {
                    const codes = g.perms.map(code);
                    const n = codes.filter((c) => has(role.name, c)).length;
                    const full = n === codes.length;
                    return (
                      <section key={g.key} className={styles.groupCard} aria-label={g.name}>
                        <div className={styles.groupHead}>
                          <span className={styles.groupName}>{g.name}</span>
                          <span className={`${styles.countPill} ${n === 0 ? styles.countNone : full ? styles.countFull : styles.countSome}`}>
                            {n}/{codes.length}
                          </span>
                          <span className={styles.spacer} />
                          <span className={styles.allLabel}>Todo</span>
                          <Switch
                            on={full}
                            locked={LOCKED.has(role.name)}
                            disabled={!editable(role.name)}
                            label={`${full ? 'Quitar' : 'Dar'} todo ${g.name}`}
                            onToggle={() => setPerms(role.name, codes, !full)}
                          />
                        </div>
                        {g.rows.map((p) => (
                          <div key={p.id} className={styles.permRow}>
                            <div className={styles.permText}>
                              <span className={styles.permLabel}>{p.description}</span>
                              <code className={styles.permCode}>{code(p)}</code>
                            </div>
                            <Switch
                              on={has(role.name, code(p))}
                              changed={changed(role.name, code(p))}
                              locked={LOCKED.has(role.name)}
                              disabled={!editable(role.name)}
                              label={p.description}
                              onToggle={() => setPerms(role.name, [code(p)], !has(role.name, code(p)))}
                            />
                          </div>
                        ))}
                      </section>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.cmpBar}>
                <button type="button" className={`${styles.btn} ${styles.btnBack}`} onClick={() => setComparing(false)}>
                  <CaretLeft size={15} weight="bold" />Volver
                </button>
                <span className={styles.cmpLabel}>Comparando</span>
                {cmp.map((r) => (
                  <span key={r} className={styles.cmpChip}>
                    {roleLabel(r)}
                    <button type="button" onClick={() => setCmp(cmp.filter((x) => x !== r))} aria-label={`Quitar ${roleLabel(r)}`}>
                      <X size={11} weight="bold" />
                    </button>
                  </span>
                ))}
                {cmp.length < MAX_CMP && cmp.length < roles.length && (
                  <select
                    className={styles.cmpAdd}
                    value=""
                    aria-label="Añadir rol a la comparación"
                    onChange={(e) => e.target.value && setCmp([...cmp, e.target.value])}
                  >
                    <option value="">+ Añadir rol</option>
                    {ordered.filter((r) => !cmp.includes(r.name)).map((r) => (
                      <option key={r.id} value={r.name}>{roleLabel(r.name)}</option>
                    ))}
                  </select>
                )}
                <span className={styles.spacer} />
                <div className={styles.segmented} role="group" aria-label="Qué permisos enseñar">
                  <button type="button" className={!onlyDiff ? styles.segOn : ''} aria-pressed={!onlyDiff} onClick={() => setOnlyDiff(false)}>Todos</button>
                  <button type="button" className={onlyDiff ? styles.segOn : ''} aria-pressed={onlyDiff} onClick={() => setOnlyDiff(true)}>Solo diferencias</button>
                </div>
              </div>

              <div className={styles.cmpTable} style={cmpCols}>
                <div className={styles.cmpHead}>
                  <div className={styles.cmpHeadFirst}>Permiso</div>
                  {cmp.map((r) => {
                    const n = cur(r).size;
                    return (
                      <div key={r} className={`${styles.cmpCol} ${r === 'superadmin' ? styles.cmpColFixed : ''}`}>
                        <div className={styles.cmpColName}>
                          {roleLabel(r)}
                          {LOCKED.has(r) && <LockSimple size={12} weight="bold" className={styles.lockIcon} aria-label="No editable" />}
                        </div>
                        <code className={styles.cmpColCode}>{r}</code>
                        <div className={styles.cmpBarTrack}>
                          <div className={styles.cmpBarFill} style={{ width: `${total ? Math.round((n / total) * 100) : 0}%` }} />
                        </div>
                        <div className={styles.cmpColCount}>{n} / {total}</div>
                      </div>
                    );
                  })}
                </div>

                {cmpGroups.map((g) => {
                  const open = !closed[g.key];
                  const codes = g.perms.map(code);
                  return (
                    <div key={g.key}>
                      <div className={styles.cmpGroupRow}>
                        <button
                          type="button"
                          className={styles.cmpGroupBtn}
                          aria-expanded={open}
                          onClick={() => setClosed({ ...closed, [g.key]: open })}
                        >
                          <CaretDown size={14} weight="bold" className={open ? '' : styles.caretClosed} />
                          <span className={styles.cmpGroupName}>{g.name}</span>
                          <span className={styles.cmpGroupTotal}>{g.perms.length === 1 ? '1 permiso' : `${g.perms.length} permisos`}</span>
                        </button>
                        {cmp.map((r) => {
                          const n = codes.filter((c) => has(r, c)).length;
                          const full = n === codes.length;
                          return (
                            <div key={r} className={styles.cmpCell}>
                              <button
                                type="button"
                                className={`${styles.cmpGroupPill} ${n === 0 ? styles.countNone : full ? styles.countFull : styles.countSome}`}
                                disabled={!editable(r)}
                                title={LOCKED.has(r) ? LOCK_REASON[r] : `${full ? 'Quitar' : 'Dar'} todo ${g.name} a ${roleLabel(r)}`}
                                onClick={() => setPerms(r, codes, !full)}
                              >
                                {n}/{codes.length}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      {open && g.rows.map((p) => (
                        <div key={p.id} className={styles.cmpPermRow}>
                          <div className={styles.cmpPermText}>
                            <span className={styles.permLabel}>{p.description}</span>
                            <code className={styles.permCode}>{code(p)}</code>
                          </div>
                          {cmp.map((r) => {
                            const on = has(r, code(p));
                            return (
                              <div key={r} className={`${styles.cmpCell} ${styles.cmpCellLight}`}>
                                <button
                                  type="button"
                                  role="checkbox"
                                  aria-checked={on}
                                  aria-label={`${p.description} · ${roleLabel(r)}`}
                                  title={LOCKED.has(r) ? LOCK_REASON[r] : `${on ? 'Quitar' : 'Dar'} a ${roleLabel(r)}`}
                                  className={[
                                    styles.check,
                                    LOCKED.has(r) ? (on ? styles.checkLockedOn : styles.checkLockedOff) : on ? styles.checkOn : '',
                                    changed(r, code(p)) ? styles.changed : '',
                                  ].join(' ')}
                                  disabled={!editable(r)}
                                  onClick={() => setPerms(r, [code(p)], !on)}
                                >
                                  <Check size={14} weight="bold" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  );
                })}
                {cmp.length === 0 && <div className={styles.cmpEmpty}>Añade un rol para compararlo.</div>}
                {cmp.length > 0 && cmpGroups.length === 0 && (
                  <div className={styles.cmpEmpty}>Estos roles tienen exactamente los mismos permisos.</div>
                )}
              </div>
            </>
          )}

          {canEdit && nChanges > 0 && (
            <div className={styles.saveBar} role="region" aria-label="Cambios sin guardar">
              <span className={styles.saveDot} />
              <span className={styles.saveText}>
                {nChanges === 1 ? '1 cambio sin guardar' : `${nChanges} cambios sin guardar`} en{' '}
                {dirtyRoles.length === 1 ? '1 rol' : `${dirtyRoles.length} roles`}
              </span>
              <span className={styles.spacer} />
              <button type="button" className={styles.discardBtn} onClick={() => { setDraft({}); setError(null); }} disabled={saving}>
                Descartar
              </button>
              <button type="button" className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          )}
        </div>
      </div>

      {auditOpen && <AuditDrawer roles={roles} perms={allPerms} onClose={() => setAuditOpen(false)} />}
      {dialog}
    </div>
  );
}

function Switch({ on, changed = false, locked, disabled, label, onToggle }: {
  on: boolean;
  changed?: boolean;
  locked: boolean;
  disabled: boolean;
  label: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onToggle}
      className={[
        styles.switch,
        on ? (locked ? styles.switchLockedOn : styles.switchOn) : '',
        changed ? styles.changed : '',
      ].join(' ')}
    >
      <span className={styles.knob} />
    </button>
  );
}

/** «Ver auditoría» (D2): los cambios de permisos por rol, de `admin_audit`.
 *  Solo se monta con `system:view_audit`; el backend lo exige igual. */
function AuditDrawer({ roles, perms, onClose }: {
  roles: RoleWithPermissions[];
  perms: RolePermission[];
  onClose: () => void;
}) {
  const { provider } = useAuth();
  const [items, setItems] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const token = await provider.getAccessToken();
      if (!token) return;
      try {
        setItems((await listAudit(token, { entity: 'role', limit: 100 })).items);
      } catch (e: any) {
        setError(e.message);
      }
    })();
  }, [provider]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const roleName = Object.fromEntries(roles.map((r) => [r.id, r.name]));
  const label = Object.fromEntries(perms.map((p) => [code(p), p.description]));
  const list = (j: Record<string, unknown> | null) => new Set(((j?.permissions as string[]) ?? []));

  return (
    <div className={styles.drawerBackdrop} onClick={onClose}>
      <aside className={styles.drawer} role="dialog" aria-label="Auditoría de permisos" onClick={(e) => e.stopPropagation()}>
        <div className={styles.drawerHead}>
          <ClockCounterClockwise size={18} />
          <span className={styles.drawerTitle}>Cambios de permisos</span>
          <span className={styles.spacer} />
          <button type="button" className={styles.drawerClose} onClick={onClose} aria-label="Cerrar">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className={styles.drawerBody}>
          {error && <div className={styles.error}>{error}</div>}
          {!items && !error && <div className={styles.listEmpty}>Cargando…</div>}
          {items?.length === 0 && <div className={styles.listEmpty}>Todavía no se ha cambiado ningún permiso.</div>}
          {items?.map((e) => {
            const before = list(e.beforeJson), after = list(e.afterJson);
            const given = [...after].filter((c) => !before.has(c));
            const taken = [...before].filter((c) => !after.has(c));
            return (
              <div key={e.id} className={styles.auditItem}>
                <div className={styles.auditTop}>
                  <span className={styles.auditRole}>{roleLabel(roleName[e.entityId ?? ''] ?? '') || 'Rol borrado'}</span>
                  <span className={styles.auditWhen}>
                    {new Date(e.createdAt).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>
                <div className={styles.auditWho}>{e.actorName ?? 'Sistema'}</div>
                {given.length > 0 && (
                  <div className={styles.auditLine}><span className={styles.auditGiven}>Ha dado</span> {given.map((c) => label[c] ?? c).join(' · ')}</div>
                )}
                {taken.length > 0 && (
                  <div className={styles.auditLine}><span className={styles.auditTaken}>Ha quitado</span> {taken.map((c) => label[c] ?? c).join(' · ')}</div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}
